import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useLazyFetchProductionDevicesQuery,
  useLazyGetProductionDeviceQuery,
  useLazySearchProductionDevicesQuery,
  useUpdateProductionDeviceMutation,
} from '../api/eecs-production-device.api';
import { ProductionDeviceAC, setEditingProductionDevice } from '../store/eecs-production-device.slice';
import {
  FetchProductionDevicesPayload,
  GetProductionDevicePayload,
  ProductionDeviceSearchResponse,
  SearchProductionDevicePayload,
  UpdateProductionDevicePayload,
} from '../types/eecs-production-device.types';

interface UseProductionDevice {
  getProductionDeviceHandler: (data: GetProductionDevicePayload) => Promise<boolean>;
  updateProductionDeviceHandler: (data: UpdateProductionDevicePayload) => Promise<boolean>;
  fetchProductionDevicesHandler: (data: FetchProductionDevicesPayload) => Promise<boolean>;
  searchProductionDevicesHandler: (payload: SearchProductionDevicePayload) => Promise<ProductionDeviceSearchResponse[]>;
  clearProductionDevicesState: () => void;
}

export const useProductionDevice = (): UseProductionDevice => {
  const dispatch = useAppDispatch();

  const [getProductionDevice] = useLazyGetProductionDeviceQuery();
  const [updateProductionDevice] = useUpdateProductionDeviceMutation();
  const [fetchProductionDevices] = useLazyFetchProductionDevicesQuery();
  const [searchProductionDevices] = useLazySearchProductionDevicesQuery();

  const getProductionDeviceHandler = async (payload: GetProductionDevicePayload) => {
    dispatch(ProductionDeviceAC.toggleLoading(true));
    const { data: response, error } = await getProductionDevice(payload);
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Production device!');
    } else {
      dispatch(setEditingProductionDevice(response.data));
    }

    dispatch(ProductionDeviceAC.toggleLoading(false));
    return !isError;
  };

  const updateProductionDeviceHandler = async (payload: UpdateProductionDevicePayload) => {
    dispatch(ProductionDeviceAC.toggleLoading(true));
    const { data: response, error } = await updateProductionDevice(payload);
    const isError = error || !response?.data?.id;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to save Production device!');
    } else {
      dispatch(setEditingProductionDevice(response.data));
    }

    dispatch(ProductionDeviceAC.toggleLoading(false));

    return !isError;
  };

  const fetchProductionDevicesHandler = async (payload: FetchProductionDevicesPayload) => {
    dispatch(ProductionDeviceAC.toggleLoading(true));
    const { data: response, error } = await fetchProductionDevices(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Production devices!');
    } else {
      dispatch(ProductionDeviceAC.clearItems());
      dispatch(ProductionDeviceAC.setItems(response.data));
      dispatch(ProductionDeviceAC.setPaginationMeta(response.meta));
    }

    dispatch(ProductionDeviceAC.toggleLoading(false));
    return !isError;
  };

  const searchProductionDevicesHandler = async (payload: SearchProductionDevicePayload) => {
    const { data: response } = await searchProductionDevices(payload);
    if (response?.data) return response.data;
    return [];
  };

  const clearProductionDevicesState = () => {
    batch(() => {
      dispatch(ProductionDeviceAC.clearItems());
      dispatch(ProductionDeviceAC.clearFilters());
      dispatch(ProductionDeviceAC.clearSorts());
      dispatch(ProductionDeviceAC.setCurrentPage(1));
    });
  };

  return {
    getProductionDeviceHandler,
    updateProductionDeviceHandler,
    fetchProductionDevicesHandler,
    searchProductionDevicesHandler,
    clearProductionDevicesState,
  };
};
